/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { useGlobalState } from "./GlobalStateProvider"
import { FloatingBox } from "./Misc/FloatingBox"
import { Button, Grid, isBlank, Spacer, TextGroupSmall } from "@cd2/components"
import { brandColors } from "./EOSThemeProvider"
import { P, TextBlock } from "@cd2/text"
import { TextWithNewLines } from "./TextWithNewLines"
import { formatDate, IComponentProp } from "@cd2/tools"
import { Fields } from "@cd2/forms"
import { icons } from "../constants/icons"
import { IRecord } from "../types/schema"
import { useEffect, useRef, useState } from "react"

type IComment = IRecord<"comments", "body" | "user" | "created_at" | "alias">

export function CommentsForm({
  comments,
  onCommentsSubmit,
  title,
  readOnly,
  maxMessageContainerHeight,
}: {
  comments: IComment[]
  onCommentsSubmit(body: string): void
  title?: {
    heading?: IComponentProp<typeof TextGroupSmall, "heading">
    body?: IComponentProp<typeof TextGroupSmall, "body">
  }
  readOnly?: boolean
  maxMessageContainerHeight?: number
}) {
  const commentsScrollRef = useRef<HTMLDivElement>(null)
  const [body, setBody] = useState("")

  // Scroll to bottom of the comments on load and when new comments are added
  useEffect(() => {
    if (commentsScrollRef.current) {
      commentsScrollRef.current.scrollTop = commentsScrollRef.current.scrollHeight
    }
  }, [comments.length])

  function handleCommentSubmit() {
    if (!isBlank(body)) {
      onCommentsSubmit(body)
      setBody("")
    }
  }

  return (
    <FloatingBox
      header={<TextGroupSmall heading="Comments" body="Leave comments below" {...title} />}
      css={css`
        padding: 0;
        ${FloatingBox.vars.childPadding}: 0 16px;
      `}
      footer={
        readOnly ? undefined : (
          <div>
            <Fields.TextArea
              onChange={setBody}
              value={body}
              rows="auto"
              placeholder="Leave a comment..."
            />
            <Spacer size={8} />
            <div>
              <Button leftIcon={icons.mail} onClick={handleCommentSubmit}>
                Send
              </Button>
            </div>
          </div>
        )
      }
    >
      <div
        ref={commentsScrollRef}
        css={css`
          max-height: ${maxMessageContainerHeight ?? 400}px;
          overflow-y: scroll;
        `}
      >
        <Spacer />
        <Grid gap={8}>
          {comments.length === 0 && (
            <TextWithNewLines text={"There are currently no messages to show."} />
          )}
          <CommentsDisplay comments={comments} />
          <Spacer />
        </Grid>
      </div>
    </FloatingBox>
  )
}

export function CommentsDisplay({ comments }: { comments: IComment[] }) {
  const { currentUser } = useGlobalState()
  return (
    <Grid gap={8}>
      {comments.map(comment => (
        <FloatingBox
          key={comment.id}
          css={css`
            width: 90%;
            margin: ${currentUser.id === comment.user?.id ? "0 0 0 auto" : "0 auto 0 0"};
            color: white;
          `}
          color={
            currentUser.id === comment.user?.id
              ? brandColors.blueLightBackground
              : brandColors.greyLightBackground
          }
        >
          <TextBlock>
            <TextWithNewLines text={comment.body} />
            <P size="small" color="rgba(0,0,0,0.7)">
              Sent by {currentUser.id === comment.user?.id ? "you" : comment.alias} at{" "}
              {formatDate(comment.created_at!, "dd/MM/yyyy HH:mm")}
            </P>
          </TextBlock>
        </FloatingBox>
      ))}
    </Grid>
  )
}
